import React, { useState, useRef } from 'react'
import { getRowHeightVariable } from 'components/spreadsheet/helpers/functions'
import { formatAttachmentText } from 'components/spreadsheet/helpers/format'
import { IContextMenuState, IFile, IFileParentResource } from 'types'
import { INITIAL_CONTEXT_MENU_STATE } from 'app-constants'
import api from 'helpers/api'
import AttachmentCellModal from 'components/spreadsheet/components/modal/views/attachment'
import { useApplicationStore } from 'hooks/application'
import Button from 'components/button'
import { useDataContext } from 'components/spreadsheet/contexts/data/useDataContext'
import { Plus, Visible } from 'components/icons'
import { cellStyles } from 'components/spreadsheet/components/cell/styles'
import Spinner from 'components/spinner'

interface AttachmentCellProps {
  value: IFile[]
  align: 'center' | 'left' | 'right'
  color: string
  readOnly: boolean
  isAdmin: boolean
  isContributor: boolean
  rowHeight: number
  selected: boolean
  columnId: string | undefined
  isExpanded?: boolean
  setCellValue: (value: IFile[], onSuccess: () => void, onError: () => void) => void
}

const styles = cellStyles()

const AttachmentCellComponent: React.FC<AttachmentCellProps> = ({
  value,
  align,
  color,
  readOnly,
  isAdmin,
  isContributor,
  rowHeight,
  selected,
  isExpanded,
  setCellValue,
  columnId
}) => {
  const inputRef = useRef<HTMLDivElement>(null)
  const { spreadsheetData } = useDataContext()
  const attachmentUploaderInputRef = useRef<HTMLInputElement>(null)
  const [attachmentMenu, setAttachmentMenu] = useState<IContextMenuState>(INITIAL_CONTEXT_MENU_STATE)
  const { setSnackbarMessage, displayErrorMessage } = useApplicationStore()
  const [processing, setProcessing] = useState<boolean>(false)
  // hideInput allows to (re)render a input element every time a file is uploaded.
  // This allows the onchange event to be fired in the case a user deletes a file and chooses to upload the same file again.
  const [hideInput, setHideInput] = useState<boolean>(false)
  const [attachmentModal, setAttachmentModal] = useState<boolean>(false)

  const uploadFile = async (file: File) => {
    const resources: IFileParentResource[] = [
      { resource: 'document_table', publicId: spreadsheetData!.tableDetails.publicId }
    ]

    try {
      const urlData = await api.uploadFile(file, resources)
      if (urlData) {
        return urlData.data
      } else {
        setProcessing(false)
        setSnackbarMessage({
          status: 'error',
          message: "Your file couldn't be uploaded. Try again.",
          offerSupport: true
        })
        return null
      }
    } catch (error) {
      displayErrorMessage(error)
      setProcessing(false)
      return null
    }
  }

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (processing) return
    const uploadedFiles = event.target.files
    if (uploadedFiles) {
      setProcessing(true)
      const allFiles = value ? [...value] : []
      setHideInput(true)
      for (let i = 0; i < uploadedFiles.length; i++) {
        const file = uploadedFiles[i]
        const newFileUrl = await uploadFile(file)
        if (newFileUrl) allFiles.push(newFileUrl)
      }
      setHideInput(false)
      setCellValue(
        allFiles,
        () => setProcessing(false),
        () => setProcessing(false)
      )
    }
  }

  const canAddLinks = isAdmin || isContributor

  const fileSummary = formatAttachmentText(value)

  return (
    <div
      className={styles.value({ focused: selected, class: 'relative cell-value' })}
      data-focussed={selected || undefined}
      ref={inputRef}
      tabIndex={-1}
      style={{
        textAlign: align,
        color: color,
        maxHeight: `${getRowHeightVariable(rowHeight) - 3}px`,
        width: 'var(--cell-width)',
        maxWidth: 'var(--cell-width)'
      }}
      onContextMenu={() =>
        setAttachmentMenu({
          ...attachmentMenu,
          open: false
        })
      }
      onClick={(event: React.MouseEvent) => {
        event.persist()
        const { left, bottom } = event.currentTarget.getBoundingClientRect()
        setAttachmentMenu({
          ...attachmentMenu,
          open: true,
          top: `${bottom}px`,
          left: `${left}px`,
          bottom: 'auto',
          right: 'auto'
        })
      }}
      onDoubleClick={(event) => event.stopPropagation()}
    >
      <div className={`flex items-center truncate ${isExpanded ? 'w-[unset]' : 'w-[inherit]'}`}>
        {(selected || isExpanded) && !readOnly && canAddLinks && (
          <Button
            className="p-px max-w-[27px] w-[27px]"
            internalType="grey"
            onClick={() => {
              if (!processing) attachmentUploaderInputRef.current?.click()
            }}
          >
            {processing ? <Spinner className="h-[15px] w-[15px]" /> : <Plus />}
          </Button>
        )}
        {!hideInput && (
          <input type="file" ref={attachmentUploaderInputRef} onChange={handleFileChange} className="hidden" multiple />
        )}
        {Array.isArray(value) && value.length > 0 ? (
          <div className="flex items-center">
            <div
              className="inline-flex items-center relative"
              style={{
                height: 'inherit',
                maxHeight: 'inherit',
                width: isExpanded ? 400 : `calc(100% - ${selected ? 40 : 10}px)`
              }}
            >
              <span className="truncate justify-between">{fileSummary}</span>
            </div>

            {(selected || isExpanded) && (
              <div
                className="absolute cursor-pointer self-center mr-[5px] right-0"
                onClick={() => setAttachmentModal(true)}
              >
                <Visible className="w-5 h-5" />
              </div>
            )}
          </div>
        ) : (
          <span>&nbsp;</span>
        )}
      </div>
      {attachmentModal && Array.isArray(value) && value.length > 0 && (
        <AttachmentCellModal
          id="attachment-cell-modal"
          open={attachmentModal}
          setOpen={setAttachmentModal}
          files={value}
          setCellValue={setCellValue}
          isAdmin={isAdmin}
          isContributor={isContributor}
          isReadOnly={readOnly}
          columnId={columnId}
        />
      )}
    </div>
  )
}

export const AttachmentCell = AttachmentCellComponent
